import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { useScrollFrame } from '@/hooks/use-scroll-frame'

interface ScrollProgressProps {
    target?: string
}

/**
 * The slim orange line at the very top of the window. When a target selector
 * is given it tracks progress through that element instead of the whole page.
 */
export const ScrollProgress: FC<ScrollProgressProps> = ({ target }): ReactNode => {
    const barRef = useRef<HTMLDivElement>(null)

    useScrollFrame(() => {
        const bar = barRef.current

        if (!bar) return

        let progress = 0
        const element = target ? document.querySelector(target) : null

        if (element) {
            const rect = element.getBoundingClientRect()
            const distance = rect.height - window.innerHeight * 0.6

            progress = distance > 0 ? (window.innerHeight * 0.4 - rect.top) / distance : 0
        }
        else {
            const max = document.documentElement.scrollHeight - window.innerHeight

            progress = max > 0 ? window.scrollY / max : 0
        }

        bar.style.transform = `scaleX(${Math.min(1, Math.max(0, progress)).toFixed(4)})`
    })

    return (
        <div
            aria-hidden='true'
            className='pointer-events-none fixed inset-x-0 top-0 z-[95] h-[3px]'
        >
            <div
                ref={barRef}
                className='h-full w-full origin-left bg-ember'
                style={{ transform: 'scaleX(0)' }}
            />
        </div>
    )
}
